import { wifiFailureMessage } from './wifi';

const SERVICE_UUID = '6f1d0001-7b3a-4c2e-9a4d-2f8b1e0c5a90';
const SETUP_UUID = '6f1d0002-7b3a-4c2e-9a4d-2f8b1e0c5a90';
const CONTROL_UUID = '6f1d0003-7b3a-4c2e-9a4d-2f8b1e0c5a90';
const RESPONSE_UUID = '6f1d0004-7b3a-4c2e-9a4d-2f8b1e0c5a90';
const STATUS_UUID = '6f1d0005-7b3a-4c2e-9a4d-2f8b1e0c5a90';
const CHUNK_SIZE = 180;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

interface SetupInfo {
  piEphemeralPub: string;
  piHandshakeId: string;
  deviceName: string;
  protocolVersion: number;
  needsWifi: boolean;
  connectivity: string;
  unclaimed: boolean;
}

interface Chunk {
  id: string;
  seq: number;
  total: number;
  raw: string;
}

interface WireResponse {
  type: string;
  id?: string;
  ok: boolean;
  result?: any;
  error?: { code?: string; message?: string };
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  return Uint8Array.from(atob(value), (character) => character.charCodeAt(0));
}

function hexToBytes(value: string): Uint8Array {
  if (value.length % 2 !== 0 || !/^[0-9a-f]+$/i.test(value)) throw new Error('The controller setup data was invalid.');
  return Uint8Array.from({ length: value.length / 2 }, (_, index) => Number.parseInt(value.slice(index * 2, index * 2 + 2), 16));
}

function concat(...arrays: Uint8Array[]): Uint8Array {
  const result = new Uint8Array(arrays.reduce((sum, item) => sum + item.length, 0));
  let offset = 0;
  for (const item of arrays) {
    result.set(item, offset);
    offset += item.length;
  }
  return result;
}

function ownedBuffer(value: Uint8Array): ArrayBuffer {
  return Uint8Array.from(value).buffer;
}

function chunks(payload: Uint8Array): Chunk[] {
  const total = Math.max(1, Math.ceil(payload.length / CHUNK_SIZE));
  const id = Array.from(crypto.getRandomValues(new Uint8Array(8)), (byte) => byte.toString(16).padStart(2, '0')).join('');
  return Array.from({ length: total }, (_, seq) => ({
    id,
    seq,
    total,
    raw: bytesToBase64(payload.slice(seq * CHUNK_SIZE, (seq + 1) * CHUNK_SIZE)),
  }));
}

class Reassembler {
  private inflight = new Map<string, { total: number; parts: Map<number, Uint8Array> }>();

  add(chunk: Chunk): Uint8Array | null {
    if (!chunk.id || chunk.total < 1 || chunk.total > 4096 || chunk.seq < 0 || chunk.seq >= chunk.total) return null;
    const entry = this.inflight.get(chunk.id) ?? { total: chunk.total, parts: new Map() };
    if (entry.total !== chunk.total || entry.parts.has(chunk.seq)) return null;
    entry.parts.set(chunk.seq, base64ToBytes(chunk.raw));
    this.inflight.set(chunk.id, entry);
    if (entry.parts.size !== entry.total) return null;
    const value = concat(...Array.from({ length: entry.total }, (_, index) => entry.parts.get(index) ?? new Uint8Array()));
    this.inflight.delete(chunk.id);
    return value;
  }
}

export interface BluetoothProgress {
  stage: string;
  message: string;
  detail?: string;
}

export interface BluetoothSetupInput {
  setupCode: string;
  ssid?: string;
  psk?: string;
  hidden?: boolean;
  security?: 'wpa2' | 'open';
  claimCode: string;
  appDeviceId: string;
  displayName: string;
}

export interface BluetoothSetupResult {
  deviceId: string;
  deviceName: string;
  lanToken: string;
  lanTokenVersion: number;
  certFingerprint: string;
  protocolVersion?: number;
  ip?: string;
}

export type BluetoothConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed';

export interface BluetoothControlConnectInput {
  setupCode: string;
  appDeviceId: string;
  displayName: string;
}

export interface BluetoothNotification {
  type: 'notification';
  name: string;
  data?: any;
}

/** Prefer a previously permitted controller so reconnects do not reopen the native chooser. */
export async function selectBluetoothDevice(bluetooth: Bluetooth, preferredId = ''): Promise<BluetoothDevice> {
  const permitted = await bluetooth.getDevices?.() ?? [];
  const remembered = permitted.find((device) => device.id === preferredId);
  if (remembered) return remembered;
  if (!preferredId && permitted.length === 1) return permitted[0];
  return bluetooth.requestDevice({ filters: [{ services: [SERVICE_UUID] }] });
}

export class WebBluetoothProvisioner {
  private device?: BluetoothDevice;
  private control?: BluetoothRemoteGATTCharacteristic;
  private response?: BluetoothRemoteGATTCharacteristic;
  private status?: BluetoothRemoteGATTCharacteristic;
  private key?: CryptoKey;
  private responseFrames = new Reassembler();
  private statusFrames = new Reassembler();
  private pending = new Map<string, { resolve: (response: WireResponse) => void; reject: (error: Error) => void; timer: number }>();
  onProgress?: (progress: BluetoothProgress) => void;

  static get supported(): boolean {
    return window.isSecureContext && navigator.bluetooth != null;
  }

  async run(input: BluetoothSetupInput): Promise<BluetoothSetupResult> {
    if (!WebBluetoothProvisioner.supported || !navigator.bluetooth) throw new Error('Web Bluetooth is not available in this browser.');
    this.onProgress?.({ stage: 'finding', message: 'Choose the controller nearby' });
    this.device = await navigator.bluetooth.requestDevice({ filters: [{ services: [SERVICE_UUID] }] });
    const server = await this.device.gatt?.connect();
    if (!server) throw new Error('The Bluetooth connection could not be opened.');
    const service = await server.getPrimaryService(SERVICE_UUID);
    const setupCharacteristic = await service.getCharacteristic(SETUP_UUID);
    this.control = await service.getCharacteristic(CONTROL_UUID);
    this.response = await service.getCharacteristic(RESPONSE_UUID);
    this.status = await service.getCharacteristic(STATUS_UUID);
    await this.response.startNotifications();
    await this.status.startNotifications();
    this.response.addEventListener('characteristicvaluechanged', (event) => void this.handleResponse(event));
    this.status.addEventListener('characteristicvaluechanged', (event) => this.handleStatus(event));

    const setupView = await setupCharacteristic.readValue();
    const setup = JSON.parse(decoder.decode(setupView.buffer.slice(setupView.byteOffset, setupView.byteOffset + setupView.byteLength))) as SetupInfo;
    this.onProgress?.({ stage: 'securing', message: `Securing ${setup.deviceName || this.device.name || 'controller'}` });
    const pair = await this.handshake(setup, input);
    let ip: string | undefined;
    if (setup.needsWifi || input.ssid) {
      if (!input.ssid?.trim()) throw new Error('This controller needs a Wi-Fi network name.');
      this.onProgress?.({ stage: 'wifi', message: `Joining ${input.ssid}` });
      const wifi = await this.call<{ ip: string; joined: boolean }>('provision_wifi', {
        ssid: input.ssid.trim(),
        psk: input.psk ?? '',
        hidden: input.hidden ?? false,
        security: input.security ?? '',
      });
      ip = wifi.ip;
    }
    this.onProgress?.({ stage: 'claiming', message: 'Claiming the controller' });
    await this.call('claim_handoff', { claimCode: input.claimCode }, 60_000);
    await this.call('end_setup').catch(() => undefined);
    this.onProgress?.({ stage: 'done', message: 'Controller setup is complete' });
    this.device.gatt?.disconnect();
    return { ...pair, ip };
  }

  private async handshake(setup: SetupInfo, input: BluetoothSetupInput): Promise<BluetoothSetupResult> {
    const privateKey = await crypto.subtle.generateKey({ name: 'X25519' }, true, ['deriveBits']) as CryptoKeyPair;
    const appPub = new Uint8Array(await crypto.subtle.exportKey('raw', privateKey.publicKey));
    const piPub = base64ToBytes(setup.piEphemeralPub);
    const piKey = await crypto.subtle.importKey('raw', ownedBuffer(piPub), { name: 'X25519' }, false, []);
    const shared = new Uint8Array(await crypto.subtle.deriveBits({ name: 'X25519', public: piKey }, privateKey.privateKey, 256));
    const setupSecret = new Uint8Array(
      await crypto.subtle.digest('SHA-256', encoder.encode(`nuance-venue-setup-code:v1\n${input.setupCode}`)),
    );
    const handshakeId = hexToBytes(setup.piHandshakeId);
    const material = await crypto.subtle.importKey('raw', ownedBuffer(concat(shared, setupSecret)), 'HKDF', false, ['deriveKey']);
    this.key = await crypto.subtle.deriveKey(
      { name: 'HKDF', hash: 'SHA-256', salt: ownedBuffer(handshakeId), info: ownedBuffer(encoder.encode('nuance-venue-ble-provision:v1')) },
      material,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
    const hmacKey = await crypto.subtle.importKey('raw', ownedBuffer(setupSecret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const proof = new Uint8Array(await crypto.subtle.sign('HMAC', hmacKey, ownedBuffer(concat(appPub, piPub, handshakeId))));
    const id = crypto.randomUUID();
    const response = await this.sendAndAwait(
      id,
      encoder.encode(JSON.stringify({
        id,
        appPub: bytesToBase64(appPub),
        appDeviceId: input.appDeviceId,
        displayName: input.displayName,
        proof: bytesToBase64(proof),
      })),
      false,
      30_000,
    );
    if (!response.ok) throw new Error(response.error?.message ?? 'The setup code was not accepted.');
    return response.result as BluetoothSetupResult;
  }

  async call<T = any>(name: string, params: Record<string, unknown> = {}, timeoutMs = 30_000): Promise<T> {
    const id = crypto.randomUUID();
    const response = await this.sendAndAwait(id, encoder.encode(JSON.stringify({ type: 'command', id, name, params })), true, timeoutMs);
    if (!response.ok) throw new Error(response.error?.message ?? `${name} was refused.`);
    return response.result as T;
  }

  private async seal(value: Uint8Array): Promise<Uint8Array> {
    if (!this.key) throw new Error('The secure setup channel is not ready.');
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: ownedBuffer(nonce) }, this.key, ownedBuffer(value)));
    return concat(nonce, encrypted);
  }

  private async open(value: Uint8Array): Promise<Uint8Array> {
    if (!this.key || value.length < 28) throw new Error('The secure setup response was invalid.');
    return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ownedBuffer(value.slice(0, 12)) }, this.key, ownedBuffer(value.slice(12))));
  }

  private async sendAndAwait(id: string, payload: Uint8Array, sealed: boolean, timeoutMs: number): Promise<WireResponse> {
    if (!this.control) throw new Error('The Bluetooth control channel is not ready.');
    const body = sealed ? await this.seal(payload) : payload;
    const response = new Promise<WireResponse>((resolve, reject) => {
      const timer = globalThis.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error('The controller did not respond during setup.'));
      }, timeoutMs) as unknown as number;
      this.pending.set(id, { resolve, reject, timer });
    });
    for (const chunk of chunks(body)) {
      await this.control.writeValueWithResponse(ownedBuffer(encoder.encode(JSON.stringify(chunk))));
    }
    return response;
  }

  private async handleResponse(event: Event): Promise<void> {
    const characteristic = event.target as BluetoothRemoteGATTCharacteristic;
    if (!characteristic.value) return;
    try {
      const chunk = JSON.parse(decoder.decode(characteristic.value)) as Chunk;
      const assembled = this.responseFrames.add(chunk);
      if (!assembled) return;
      let plain: Uint8Array;
      try {
        plain = await this.open(assembled);
      } catch {
        plain = assembled;
      }
      const response = JSON.parse(decoder.decode(plain)) as WireResponse;
      if (!response.id) return;
      const pending = this.pending.get(response.id);
      if (!pending) return;
      globalThis.clearTimeout(pending.timer);
      this.pending.delete(response.id);
      pending.resolve(response);
    } catch {
      return;
    }
  }

  private handleStatus(event: Event): void {
    const characteristic = event.target as BluetoothRemoteGATTCharacteristic;
    if (!characteristic.value) return;
    try {
      const chunk = JSON.parse(decoder.decode(characteristic.value)) as Chunk;
      const assembled = this.statusFrames.add(chunk);
      if (!assembled) return;
      const status = JSON.parse(decoder.decode(assembled)) as { name: string; data?: Record<string, unknown> };
      const labels: Record<string, string> = {
        wifi_joining: 'Joining the network',
        wifi_got_ip: 'Connected to Wi-Fi',
        wifi_failed: 'Could not join Wi-Fi',
        registered: 'Registering the controller',
        claim_confirmed: 'Controller claimed',
        setup_expiring: 'The setup window is closing',
      };
      const message = status.name === 'wifi_failed'
        ? wifiFailureMessage(status.data?.reason)
        : labels[status.name] ?? status.name;
      this.onProgress?.({ stage: status.name, message, detail: status.data ? JSON.stringify(status.data) : undefined });
    } catch {
      return;
    }
  }
}

/**
 * Operational Web Bluetooth transport. It authenticates through the same setup-code handshake used by
 * provisioning, then carries ordinary registry envelopes over the controller's encrypted GATT channel.
 */
export class WebBluetoothControlClient {
  private device?: BluetoothDevice;
  private control?: BluetoothRemoteGATTCharacteristic;
  private response?: BluetoothRemoteGATTCharacteristic;
  private status?: BluetoothRemoteGATTCharacteristic;
  private key?: CryptoKey;
  private responseFrames = new Reassembler();
  private statusFrames = new Reassembler();
  private pending = new Map<string, { resolve: (response: WireResponse) => void; reject: (error: Error) => void; timer: number }>();
  private intentionalClose = false;
  private reconnectTimer?: number;
  private reconnectAttempt = 0;
  private input?: BluetoothControlConnectInput;
  private notificationListeners = new Set<(notification: BluetoothNotification) => void>();

  onState?: (state: BluetoothConnectionState, reason?: string) => void;
  onNotification?: (notification: BluetoothNotification) => void;
  onIdentity?: (identity: BluetoothSetupResult) => void;

  static get supported(): boolean {
    return window.isSecureContext && navigator.bluetooth != null;
  }

  get connected(): boolean {
    return Boolean(this.key && this.device?.gatt?.connected);
  }

  get deviceName(): string {
    return this.device?.name ?? '';
  }

  subscribeNotifications(listener: (notification: BluetoothNotification) => void): () => void {
    this.notificationListeners.add(listener);
    return () => this.notificationListeners.delete(listener);
  }

  async connect(input: BluetoothControlConnectInput): Promise<BluetoothSetupResult> {
    if (!WebBluetoothControlClient.supported || !navigator.bluetooth) {
      throw new Error('Web Bluetooth is not available in this browser.');
    }
    this.intentionalClose = true;
    this.clearReconnect();
    this.device?.gatt?.disconnect();
    this.intentionalClose = false;
    this.input = { ...input, setupCode: input.setupCode.trim() };
    this.onState?.('connecting');
    const rememberedId = localStorage.getItem('nuance-venue:bluetooth-device') ?? '';
    try {
      this.device = await selectBluetoothDevice(navigator.bluetooth, rememberedId);
      localStorage.setItem('nuance-venue:bluetooth-device', this.device.id);
      return await this.open(this.device, this.input);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.onState?.('failed', message);
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    this.intentionalClose = true;
    this.clearReconnect();
    this.rejectPending('Bluetooth disconnected by operator.');
    this.key = undefined;
    this.device?.gatt?.disconnect();
    this.onState?.('idle');
  }

  async call<T = any>(name: string, params: Record<string, unknown> = {}, timeoutMs = 30_000): Promise<T> {
    if (!this.connected) throw new Error('Connect to the controller over Bluetooth first.');
    const id = crypto.randomUUID();
    const response = await this.sendAndAwait(
      id,
      encoder.encode(JSON.stringify({ type: 'command', id, name, params })),
      true,
      timeoutMs,
    );
    if (!response.ok) throw new Error(`${response.error?.code ?? 'controller-error'}: ${response.error?.message ?? `${name} was refused.`}`);
    return response.result as T;
  }

  private async open(device: BluetoothDevice, input: BluetoothControlConnectInput): Promise<BluetoothSetupResult> {
    const server = await device.gatt?.connect();
    if (!server) throw new Error('The Bluetooth connection could not be opened.');
    device.removeEventListener('gattserverdisconnected', this.handleDisconnect);
    device.addEventListener('gattserverdisconnected', this.handleDisconnect);
    const service = await server.getPrimaryService(SERVICE_UUID);
    const setupCharacteristic = await service.getCharacteristic(SETUP_UUID);
    this.control = await service.getCharacteristic(CONTROL_UUID);
    this.response = await service.getCharacteristic(RESPONSE_UUID);
    this.status = await service.getCharacteristic(STATUS_UUID);
    await this.response.startNotifications();
    await this.status.startNotifications();
    this.response.addEventListener('characteristicvaluechanged', this.handleResponse);
    this.status.addEventListener('characteristicvaluechanged', this.handleStatus);
    const setupView = await setupCharacteristic.readValue();
    const setup = JSON.parse(decoder.decode(setupView.buffer.slice(setupView.byteOffset, setupView.byteOffset + setupView.byteLength))) as SetupInfo;
    const identity = await this.handshake(setup, input);
    this.reconnectAttempt = 0;
    this.onState?.('connected');
    this.onIdentity?.(identity);
    return identity;
  }

  private async handshake(setup: SetupInfo, input: BluetoothControlConnectInput): Promise<BluetoothSetupResult> {
    const privateKey = await crypto.subtle.generateKey({ name: 'X25519' }, true, ['deriveBits']) as CryptoKeyPair;
    const appPub = new Uint8Array(await crypto.subtle.exportKey('raw', privateKey.publicKey));
    const piPub = base64ToBytes(setup.piEphemeralPub);
    const piKey = await crypto.subtle.importKey('raw', ownedBuffer(piPub), { name: 'X25519' }, false, []);
    const shared = new Uint8Array(await crypto.subtle.deriveBits({ name: 'X25519', public: piKey }, privateKey.privateKey, 256));
    const setupSecret = new Uint8Array(
      await crypto.subtle.digest('SHA-256', encoder.encode(`nuance-venue-setup-code:v1\n${input.setupCode}`)),
    );
    const handshakeId = hexToBytes(setup.piHandshakeId);
    const material = await crypto.subtle.importKey('raw', ownedBuffer(concat(shared, setupSecret)), 'HKDF', false, ['deriveKey']);
    this.key = await crypto.subtle.deriveKey(
      { name: 'HKDF', hash: 'SHA-256', salt: ownedBuffer(handshakeId), info: ownedBuffer(encoder.encode('nuance-venue-ble-provision:v1')) },
      material,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
    const hmacKey = await crypto.subtle.importKey('raw', ownedBuffer(setupSecret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const proof = new Uint8Array(await crypto.subtle.sign('HMAC', hmacKey, ownedBuffer(concat(appPub, piPub, handshakeId))));
    const id = crypto.randomUUID();
    const response = await this.sendAndAwait(
      id,
      encoder.encode(JSON.stringify({
        id,
        appPub: bytesToBase64(appPub),
        appDeviceId: input.appDeviceId,
        displayName: input.displayName,
        proof: bytesToBase64(proof),
      })),
      false,
      30_000,
    );
    if (!response.ok) {
      this.key = undefined;
      throw new Error(response.error?.message ?? 'The setup code was not accepted.');
    }
    return response.result as BluetoothSetupResult;
  }

  private async seal(value: Uint8Array): Promise<Uint8Array> {
    if (!this.key) throw new Error('The secure Bluetooth channel is not ready.');
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: ownedBuffer(nonce) }, this.key, ownedBuffer(value)));
    return concat(nonce, encrypted);
  }

  private async unseal(value: Uint8Array): Promise<Uint8Array> {
    if (!this.key || value.length < 28) throw new Error('The secure Bluetooth response was invalid.');
    return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ownedBuffer(value.slice(0, 12)) }, this.key, ownedBuffer(value.slice(12))));
  }

  private async sendAndAwait(id: string, payload: Uint8Array, sealed: boolean, timeoutMs: number): Promise<WireResponse> {
    if (!this.control) throw new Error('The Bluetooth control channel is not ready.');
    const body = sealed ? await this.seal(payload) : payload;
    const response = new Promise<WireResponse>((resolve, reject) => {
      const timer = globalThis.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error('The controller did not respond over Bluetooth.'));
      }, timeoutMs) as unknown as number;
      this.pending.set(id, { resolve, reject, timer });
    });
    for (const chunk of chunks(body)) {
      await this.control.writeValueWithResponse(ownedBuffer(encoder.encode(JSON.stringify(chunk))));
    }
    return response;
  }

  private handleResponse = (event: Event): void => {
    const characteristic = event.target as BluetoothRemoteGATTCharacteristic;
    if (!characteristic.value) return;
    void this.routeResponse(characteristic.value);
  };

  private async routeResponse(value: DataView): Promise<void> {
    try {
      const chunk = JSON.parse(decoder.decode(value)) as Chunk;
      const assembled = this.responseFrames.add(chunk);
      if (!assembled) return;
      let plain: Uint8Array;
      try {
        plain = await this.unseal(assembled);
      } catch {
        // A failed pre-key handshake is the protocol's only plaintext response.
        plain = assembled;
      }
      const response = JSON.parse(decoder.decode(plain)) as WireResponse;
      if (!response.id) return;
      const pending = this.pending.get(response.id);
      if (!pending) return;
      globalThis.clearTimeout(pending.timer);
      this.pending.delete(response.id);
      pending.resolve(response);
    } catch {
      return;
    }
  }

  private handleStatus = (event: Event): void => {
    const characteristic = event.target as BluetoothRemoteGATTCharacteristic;
    if (!characteristic.value) return;
    void this.routeStatus(characteristic.value);
  };

  private async routeStatus(value: DataView): Promise<void> {
    try {
      const chunk = JSON.parse(decoder.decode(value)) as Chunk;
      const assembled = this.statusFrames.add(chunk);
      if (!assembled) return;
      const plain = await this.unseal(assembled);
      const notification = JSON.parse(decoder.decode(plain)) as BluetoothNotification;
      if (notification.name) {
        this.onNotification?.(notification);
        for (const listener of this.notificationListeners) listener(notification);
      }
    } catch {
      return;
    }
  }

  private handleDisconnect = (): void => {
    this.key = undefined;
    this.rejectPending('The Bluetooth connection was interrupted.');
    if (this.intentionalClose || !this.device || !this.input) {
      this.onState?.('idle');
      return;
    }
    this.scheduleReconnect();
  };

  private scheduleReconnect(): void {
    if (!this.device || !this.input || this.intentionalClose || this.reconnectTimer != null) return;
    this.reconnectAttempt += 1;
    this.onState?.('reconnecting', 'Bluetooth connection interrupted.');
    const wait = Math.min(12_000, 600 * 2 ** (this.reconnectAttempt - 1));
    this.reconnectTimer = globalThis.setTimeout(() => {
      this.reconnectTimer = undefined;
      void this.open(this.device!, this.input!).catch((error: unknown) => {
        this.onState?.('reconnecting', error instanceof Error ? error.message : String(error));
        this.scheduleReconnect();
      });
    }, wait) as unknown as number;
  }

  private rejectPending(message: string): void {
    for (const pending of this.pending.values()) {
      globalThis.clearTimeout(pending.timer);
      pending.reject(new Error(message));
    }
    this.pending.clear();
  }

  private clearReconnect(): void {
    if (this.reconnectTimer != null) globalThis.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
  }
}
